/**
 * 字牌（跑胡子）官方规则与判定解算引擎
 */
import {
  Card,
  CardType,
  CardValue,
  Meld,
  MeldType,
  ChiOption,
  WinResult
} from './types';
import { DEFAULT_RULE_CONFIG, resolveRuleConfig, RuleConfig } from './rules.config';

export class Rules {
  // 默认起胡胡息
  public static readonly DEFAULT_MIN_HUXI = DEFAULT_RULE_CONFIG.minHuxiToWin;

  /**
   * 创建一副标准80张字牌
   */
  public static createDeck(config: RuleConfig = DEFAULT_RULE_CONFIG): Card[] {
    const rule = resolveRuleConfig(config);
    const deck: Card[] = [];
    const types: CardType[] = ['small', 'big'];

    types.forEach(type => {
      for (let value = 1; value <= 10; value++) {
        for (let copy = 0; copy < 4; copy++) {
          const isBig = type === 'big';
          const isRed = rule.redCardValues.includes(value); // 二、七、十（大小字）为红
          const name = this.getCardName(type, value as CardValue);
          const pinyin = this.getCardPinyin(type, value as CardValue);

          deck.push({
            id: `${type}_${value}_${copy}_${Math.random().toString(36).substring(2, 7)}`,
            type,
            value: value as CardValue,
            isBig,
            isRed,
            name,
            pinyin
          });
        }
      }
    });

    return deck;
  }

  /**
   * 洗牌 (Fisher-Yates 算法)
   */
  public static shuffleDeck(deck: Card[]): Card[] {
    const shuffled = [...deck];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  /**
   * 获取牌名汉字
   */
  public static getCardName(type: CardType, value: CardValue): string {
    const bigNames: Record<number, string> = {
      1: '壹', 2: '贰', 3: '叁', 4: '肆', 5: '伍',
      6: '陆', 7: '柒', 8: '捌', 9: '玖', 10: '拾'
    };
    const smallNames: Record<number, string> = {
      1: '一', 2: '二', 3: '三', 4: '四', 5: '五',
      6: '六', 7: '七', 8: '八', 9: '九', 10: '十'
    };
    return type === 'big' ? bigNames[value] : smallNames[value];
  }

  public static getCardPinyin(type: CardType, value: CardValue): string {
    const pinyins: Record<number, string> = {
      1: 'yi', 2: 'er', 3: 'san', 4: 'si', 5: 'wu',
      6: 'liu', 7: 'qi', 8: 'ba', 9: 'jiu', 10: 'shi'
    };
    return `${type}_${pinyins[value]}`;
  }

  /**
   * 判断两张牌是否完全同名同字
   */
  public static isSameCard(a: Card, b: Card): boolean {
    return a.type === b.type && a.value === b.value;
  }

  /**
   * 获取面子组合的胡息数
   */
  public static getMeldHuxi(type: MeldType, isBig: boolean, config: RuleConfig = DEFAULT_RULE_CONFIG): number {
    const rule = resolveRuleConfig(config);
    return rule.huxi[type][isBig ? 'big' : 'small'];
  }

  /**
   * 检查是否可以“提”
   * 1. 摸牌时：摸牌者手中已有 3 张相同牌（坎）
   * 2. 起手发牌时：手中有 4 张相同牌
   */
  public static canTi(hand: Card[], drawnCard: Card | null = null): Card | null {
    if (drawnCard) {
      const matchCount = hand.filter(c => this.isSameCard(c, drawnCard)).length;
      if (matchCount === 3) return drawnCard;
    } else {
      // 检查起手手牌中的4张
      const counts = this.countCards(hand);
      for (const [key, count] of counts.entries()) {
        if (count === 4) {
          return hand.find(c => `${c.type}_${c.value}` === key) || null;
        }
      }
    }
    return null;
  }

  /**
   * 检查是否可以“扫”（摸牌者手中有2张，摸到第3张）
   */
  public static canSao(hand: Card[], drawnCard: Card): boolean {
    const matchCount = hand.filter(c => this.isSameCard(c, drawnCard)).length;
    return matchCount === 2;
  }

  /**
   * 检查是否可以“跑”
   * 1. 之前已“碰”过3张 (明面子)：只有面子所属玩家自己摸到第4张才能跑
   * 2. 之前已“扫”过3张 (暗面子)：别人来的第4张才能跑，自己摸到的第4张应升级为提
   * 3. 手中有3张 (暗坎)：摸出或别人打出第4张可以跑
   */
  public static canPao(
    hand: Card[],
    melds: Meld[],
    targetCard: Card,
    isDrawn: boolean = true,
    isActor: boolean = true
  ): boolean {
    // 1. 检查已碰的明面子：只有面子所属玩家自己摸出的牌才能跑
    const hasPeng = melds.some(
      m => m.type === 'PENG' && m.cards.length >= 3 && this.isSameCard(m.cards[0], targetCard)
    );
    if (hasPeng && isDrawn && isActor) return true;

    // 2. 检查扫牌 (暗面子)：别人来的第4张跑，自己摸到则应升级为提
    const hasSao = melds.some(
      m => m.type === 'SAO' && m.cards.length >= 3 && this.isSameCard(m.cards[0], targetCard)
    );
    if (hasSao && (!isDrawn || !isActor)) return true;

    // 3. 检查手牌中是否有坎 (3张暗坎)：摸出或打出第4张均可跑
    const countInHand = hand.filter(c => this.isSameCard(c, targetCard)).length;
    return countInHand === 3;
  }

  /** 扫牌面子所属玩家自己摸到第四张时升级为提。 */
  public static canTiFromSao(melds: Meld[], drawnCard: Card): boolean {
    return melds.some(
      m => m.type === 'SAO' && m.cards.length >= 3 && this.isSameCard(m.cards[0], drawnCard)
    );
  }

  /**
   * 检查是否可以“碰”（非摸牌者手中有2张，场上出现第3张）
   */
  public static canPeng(hand: Card[], targetCard: Card): boolean {
    const count = hand.filter(c => this.isSameCard(c, targetCard)).length;
    return count === 2;
  }

  /**
   * 寻找所有合法的“吃”牌组合
   * 包含：
   * 1. 顺子 (target, target+1, target+2 等同字类连续三张)
   * 2. 壹贰叁 (大字或小字)
   * 3. 贰柒拾 (大字或小字)
   * 4. 绞牌 (2大1小 或 1大2小 同数值)
   */
  public static getChiOptions(hand: Card[], targetCard: Card, config: RuleConfig = DEFAULT_RULE_CONFIG): ChiOption[] {
    const rule = resolveRuleConfig(config);
    const options: ChiOption[] = [];
    const tType = targetCard.type;
    const tVal = targetCard.value;
    const isBig = targetCard.isBig;

    // 辅助获取手牌中指定类型和数值的牌 (避开重复引用同一张实体牌)
    const findHandCard = (type: CardType, val: number, excludeIds: Set<string>): Card | undefined => {
      return hand.find(c => c.type === type && c.value === val && !excludeIds.has(c.id));
    };

    // 1. 1-2-3 顺子
    if ([1, 2, 3].includes(tVal)) {
      const needed = [1, 2, 3].filter(v => v !== tVal);
      const used = new Set<string>();
      const c1 = findHandCard(tType, needed[0], used);
      if (c1) used.add(c1.id);
      const c2 = findHandCard(tType, needed[1], used);
      if (c1 && c2) {
        options.push({
          id: `chi_123_${tType}`,
          type: 'CHI_123',
          targetCard,
          handCards: [c1, c2],
          meldCards: [targetCard, c1, c2].sort((a, b) => a.value - b.value),
          name: isBig ? '壹贰叁 (6胡)' : '一二三 (3胡)',
          huxi: this.getMeldHuxi('CHI_123', isBig, rule)
        });
      }
    }

    // 2. 2-7-10 顺子
    if ([2, 7, 10].includes(tVal)) {
      const needed = [2, 7, 10].filter(v => v !== tVal);
      const used = new Set<string>();
      const c1 = findHandCard(tType, needed[0], used);
      if (c1) used.add(c1.id);
      const c2 = findHandCard(tType, needed[1], used);
      if (c1 && c2) {
        options.push({
          id: `chi_2710_${tType}`,
          type: 'CHI_2710',
          targetCard,
          handCards: [c1, c2],
          meldCards: [targetCard, c1, c2].sort((a, b) => a.value - b.value),
          name: isBig ? '贰柒拾 (6胡)' : '二七十 (3胡)',
          huxi: this.getMeldHuxi('CHI_2710', isBig, rule)
        });
      }
    }

    // 3. 普通连续顺子 (排除已包含在123/2710中的同种情况)
    // 方案 A: target, target+1, target+2
    if (tVal <= 8) {
      const used = new Set<string>();
      const c1 = findHandCard(tType, tVal + 1, used);
      if (c1) used.add(c1.id);
      const c2 = findHandCard(tType, tVal + 2, used);
      if (c1 && c2) {
        // 如果是 1,2,3 已经在上面处理了，避免重复
        if (!(tVal === 1 && (options.some(o => o.type === 'CHI_123')))) {
          const mCards = [targetCard, c1, c2].sort((a, b) => a.value - b.value);
          options.push({
            id: `chi_seq_${mCards.map(c => c.name).join('')}`,
            type: 'CHI_SEQ',
            targetCard,
            handCards: [c1, c2],
            meldCards: mCards,
            name: `${mCards.map(c => c.name).join('')} (顺子 0胡)`,
            huxi: 0
          });
        }
      }
    }

    // 方案 B: target-1, target, target+1
    if (tVal >= 2 && tVal <= 9) {
      const used = new Set<string>();
      const c1 = findHandCard(tType, tVal - 1, used);
      if (c1) used.add(c1.id);
      const c2 = findHandCard(tType, tVal + 1, used);
      if (c1 && c2) {
        if (!(tVal === 2 && options.some(o => o.type === 'CHI_123'))) {
          const mCards = [c1, targetCard, c2].sort((a, b) => a.value - b.value);
          const name = mCards.map(c => c.name).join('');
          if (!options.some(o => o.meldCards.map(c => c.name).join('') === name)) {
            options.push({
              id: `chi_seq_${name}`,
              type: 'CHI_SEQ',
              targetCard,
              handCards: [c1, c2],
              meldCards: mCards,
              name: `${name} (顺子 0胡)`,
              huxi: 0
            });
          }
        }
      }
    }

    // 方案 C: target-2, target-1, target
    if (tVal >= 3) {
      const used = new Set<string>();
      const c1 = findHandCard(tType, tVal - 2, used);
      if (c1) used.add(c1.id);
      const c2 = findHandCard(tType, tVal - 1, used);
      if (c1 && c2) {
        if (!(tVal === 3 && options.some(o => o.type === 'CHI_123'))) {
          const mCards = [c1, c2, targetCard].sort((a, b) => a.value - b.value);
          const name = mCards.map(c => c.name).join('');
          if (!options.some(o => o.meldCards.map(c => c.name).join('') === name)) {
            options.push({
              id: `chi_seq_${name}`,
              type: 'CHI_SEQ',
              targetCard,
              handCards: [c1, c2],
              meldCards: mCards,
              name: `${name} (顺子 0胡)`,
              huxi: 0
            });
          }
        }
      }
    }

    // 4. 绞牌 (2大1小 或 1大2小，数值必须相同)
    const otherType: CardType = tType === 'big' ? 'small' : 'big';
    const sameTypeCount = hand.filter(c => c.type === tType && c.value === tVal);
    const otherTypeCount = hand.filter(c => c.type === otherType && c.value === tVal);

    // 绞牌组合1: 手中 2 张对侧字 (如 target=壹，手中2个'一') -> 壹一一
    if (rule.allowJiaoChi && otherTypeCount.length >= 2) {
      const c1 = otherTypeCount[0];
      const c2 = otherTypeCount[1];
      const mCards = [targetCard, c1, c2];
      const name = mCards.map(c => c.name).join('');
      options.push({
        id: `chi_jiao_${name}_${Math.random()}`,
        type: 'CHI_JIAO',
        targetCard,
        handCards: [c1, c2],
        meldCards: mCards,
        name: `绞牌: ${name} (0胡)`,
        huxi: 0
      });
    }

    // 绞牌组合2: 手中 1 张同字 + 1 张对侧字 (如 target=壹，手中1个'壹' + 1个'一') -> 壹壹一
    if (rule.allowJiaoChi && sameTypeCount.length >= 1 && otherTypeCount.length >= 1) {
      const c1 = sameTypeCount[0];
      const c2 = otherTypeCount[0];
      const mCards = [targetCard, c1, c2];
      const name = mCards.map(c => c.name).join('');
      options.push({
        id: `chi_jiao_${name}_${Math.random()}`,
        type: 'CHI_JIAO',
        targetCard,
        handCards: [c1, c2],
        meldCards: mCards,
        name: `绞牌: ${name} (0胡)`,
        huxi: 0
      });
    }

    // 吃牌必须比牌：若手中还留有同张牌，它也必须能和剩余手牌组成一组。
    return options.flatMap(option => {
      const usedIds = new Set(option.handCards.map(card => card.id));
      const usedCounts = this.countCards(option.handCards);

      // 三张同名牌必须作为完整坎保留，不能拆出其中一两张去吃牌。
      for (const [key, usedCount] of usedCounts.entries()) {
        const totalCount = hand.filter(card => `${card.type}_${card.value}` === key).length;
        if (totalCount >= 3 && usedCount < totalCount) return [];
      }

      const unboundMatchingCards = hand.filter(
        card => this.isSameCard(card, targetCard) && !usedIds.has(card.id)
      );
      if (unboundMatchingCards.length === 0) return [option];

      const availableForBi = hand.filter(
        card => !usedIds.has(card.id) && !this.isSameCard(card, targetCard)
      );
      const biCards: NonNullable<ChiOption['biCards']> = [];
      const usedBiIds = new Set<string>();
      for (const matchingCard of unboundMatchingCards) {
        const biOption = this.getChiOptions(
          availableForBi.filter(card => !usedBiIds.has(card.id)),
          matchingCard,
          rule
        )[0];
        if (!biOption) return [];

        biCards.push({
          handCards: biOption.handCards,
          meldCards: biOption.meldCards,
          type: biOption.type,
          huxi: biOption.huxi
        });
        biOption.handCards.forEach(card => usedBiIds.add(card.id));
      }

      return [{ ...option, biCards }];
    });
  }

  /**
   * 统计手牌各牌数量
   */
  private static countCards(cards: Card[]): Map<string, number> {
    const counts = new Map<string, number>();
    cards.forEach(c => {
      const key = `${c.type}_${c.value}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }

  /**
   * 全面解算字牌胡牌状态 (回溯法分解面子与将牌)
   * @param hand 玩家手牌
   * @param exposedMelds 玩家桌面已亮牌组 (提、跑、碰、吃、扫)
   * @param winCard 触发胡牌的牌 (自摸或他人打出/摸出)
   * @param isSelfDraw 是否自摸
   * @param minHuxi 起胡胡息阈值 (默认 15)
   */
  public static calculateWin(
    hand: Card[],
    exposedMelds: Meld[],
    winCard: Card | null = null,
    isSelfDraw: boolean = true,
    minHuxi: number = this.DEFAULT_MIN_HUXI,
    config: RuleConfig = DEFAULT_RULE_CONFIG
  ): WinResult | null {
    const rule = resolveRuleConfig({ ...config, minHuxiToWin: minHuxi });
    const allHandCards = winCard ? [...hand, winCard] : [...hand];
    const totalCardCount = allHandCards.length + exposedMelds.reduce((sum, meld) => sum + meld.cards.length, 0);

    // 计算已亮牌组的胡息
    let exposedHuxi = 0;
    exposedMelds.forEach(m => {
      exposedHuxi += m.huxi;
    });

    // 手牌张数必须为 3n + 2 (或满牌 7门无将 21张)
    const countMod = allHandCards.length % 3;
    if (countMod !== 2 && countMod !== 0) {
      return null;
    }

    // 收集所有可能的有效胡牌拆分
    const validDecompositions: { melds: Meld[]; eyes: Card[]; totalHuxi: number }[] = [];

    // 1. 如果是 3n + 2，必须拆出一对将牌 (对子)
    if (countMod === 2) {
      // 找出所有可以作为将牌的对子
      const cardCounts = this.countCards(allHandCards);
      for (const [key, count] of cardCounts.entries()) {
        if (count >= 2) {
          const [typeStr, valStr] = key.split('_');
          const tType = typeStr as CardType;
          const tVal = parseInt(valStr) as CardValue;

          // 拿出两张作为将牌
          const eyeCards: Card[] = [];
          const remaining: Card[] = [];
          allHandCards.forEach(c => {
            if (c.type === tType && c.value === tVal && eyeCards.length < 2) {
              eyeCards.push(c);
            } else {
              remaining.push(c);
            }
          });

          // 递归拆解剩余的手牌为 3 张一组的面子
          const solutions: Meld[][] = [];
          this.partitionTriplets(remaining, [], solutions, rule);

          for (const handMelds of solutions) {
            let handHuxi = 0;
            handMelds.forEach(m => (handHuxi += m.huxi));
            const totalHuxi = exposedHuxi + handHuxi;

            if (totalHuxi >= minHuxi) {
              validDecompositions.push({
                melds: [...exposedMelds, ...handMelds],
                eyes: eyeCards,
                totalHuxi
              });
            }
          }
        }
      }
    } else if (countMod === 0 && totalCardCount % 3 === 0) {
      // 有亮牌时，摸入胡牌牌后暗手也可能正好由三张组合组成
      const solutions: Meld[][] = [];
      this.partitionTriplets(allHandCards, [], solutions, rule);
      solutions.forEach(handMelds => {
        let handHuxi = 0;
        handMelds.forEach(m => (handHuxi += m.huxi));
        const totalHuxi = exposedHuxi + handHuxi;
        if (totalHuxi >= minHuxi) {
          validDecompositions.push({
            melds: [...exposedMelds, ...handMelds],
            eyes: [],
            totalHuxi
          });
        }
      });
    }

    if (validDecompositions.length === 0) {
      return null;
    }

    // 选取胡息最高的一组分解方案
    validDecompositions.sort((a, b) => b.totalHuxi - a.totalHuxi);
    const best = validDecompositions[0];

    // 计算名堂 (Special Patterns)
    const allCards = [...allHandCards, ...exposedMelds.flatMap(m => m.cards)];
    const redCount = allCards.filter(c => c.isRed).length;
    const specialPatterns: string[] = [];

    // 红胡判定 (红字 >= 10)
    if (redCount >= 13) {
      specialPatterns.push('大红胡 (红字≥13)');
    } else if (redCount >= 10) {
      specialPatterns.push('红胡 (红字≥10)');
    } else if (redCount === 1) {
      specialPatterns.push('点胡 (仅1张红字)');
    } else if (redCount === 0) {
      specialPatterns.push('黑胡/乌胡 (纯黑无红)');
    }

    // 自摸判定
    if (isSelfDraw) {
      specialPatterns.push('自摸 (+1番)');
    }

    // 碰碰胡 (无顺子，全是对子/坎/碰/提/跑)
    const isAllTriples = best.melds.every(m => ['TI', 'PAO', 'KAN', 'SAO', 'PENG'].includes(m.type));
    if (isAllTriples) {
      specialPatterns.push('碰碰胡/坎坎胡');
    }

    return {
      isWin: true,
      winnerId: '',
      winCard: winCard || allHandCards[allHandCards.length - 1],
      isSelfDraw,
      totalHuxi: best.totalHuxi,
      melds: best.melds,
      eyes: best.eyes,
      specialPatterns,
      scoreDelta: {}
    };
  }

  /** 处理只剩目标牌对子、碰牌后完成整手牌的终局。 */
  public static calculatePengWin(
    hand: Card[],
    exposedMelds: Meld[],
    targetCard: Card,
    minHuxi: number = this.DEFAULT_MIN_HUXI,
    config: RuleConfig = DEFAULT_RULE_CONFIG
  ): WinResult | null {
    if (hand.length !== 2 || !hand.every(card => this.isSameCard(card, targetCard))) {
      return null;
    }

    const rule = resolveRuleConfig({ ...config, minHuxiToWin: minHuxi });
    const peng: Meld = {
      id: `peng_win_${targetCard.id}`,
      type: 'PENG',
      cards: [...hand, targetCard],
      huxi: this.getMeldHuxi('PENG', targetCard.isBig, rule),
      isExposed: true
    };
    const totalHuxi = exposedMelds.reduce((sum, meld) => sum + meld.huxi, 0) + peng.huxi;
    if (totalHuxi < minHuxi) return null;

    return {
      isWin: true,
      winnerId: '',
      winCard: targetCard,
      isSelfDraw: false,
      totalHuxi,
      melds: [...exposedMelds, peng],
      eyes: [],
      specialPatterns: ['碰碰胡/坎坎胡'],
      scoreDelta: {}
    };
  }

  /**
   * 递归将牌组拆解为 3 张一组的面子
   */
  private static partitionTriplets(cards: Card[], currentMelds: Meld[], results: Meld[][], config: RuleConfig = DEFAULT_RULE_CONFIG): void {
    if (cards.length === 0) {
      results.push([...currentMelds]);
      return;
    }

    // 取第一张牌进行匹配
    const first = cards[0];
    const rest = cards.slice(1);
    const fType = first.type;
    const fVal = first.value;
    const isBig = first.isBig;

    // 1. 尝试组成暗坎 (3张相同)
    const sameCount = rest.filter(c => this.isSameCard(c, first));
    if (sameCount.length >= 2) {
      const match1 = sameCount[0];
      const match2 = sameCount[1];
      const remainingCards = this.removeCards(cards, [first, match1, match2]);
      const meld: Meld = {
        id: `kan_${first.id}`,
        type: 'KAN',
        cards: [first, match1, match2],
        huxi: this.getMeldHuxi('KAN', isBig, config),
        isExposed: false
      };
      this.partitionTriplets(remainingCards, [...currentMelds, meld], results, config);
    }

    // 2. 尝试组成 1-2-3 (壹贰叁 / 一二三)
    if (fVal === 1) {
      const c2 = rest.find(c => c.type === fType && c.value === 2);
      const c3 = rest.find(c => c.type === fType && c.value === 3);
      if (c2 && c3) {
        const remainingCards = this.removeCards(cards, [first, c2, c3]);
        const meld: Meld = {
          id: `chi_123_${first.id}`,
          type: 'CHI_123',
          cards: [first, c2, c3],
          huxi: this.getMeldHuxi('CHI_123', isBig, config),
          isExposed: false
        };
        this.partitionTriplets(remainingCards, [...currentMelds, meld], results, config);
      }
    }

    // 3. 尝试组成 2-7-10 (贰柒拾 / 二七十)
    if (fVal === 2) {
      const c7 = rest.find(c => c.type === fType && c.value === 7);
      const c10 = rest.find(c => c.type === fType && c.value === 10);
      if (c7 && c10) {
        const remainingCards = this.removeCards(cards, [first, c7, c10]);
        const meld: Meld = {
          id: `chi_2710_${first.id}`,
          type: 'CHI_2710',
          cards: [first, c7, c10],
          huxi: this.getMeldHuxi('CHI_2710', isBig, config),
          isExposed: false
        };
        this.partitionTriplets(remainingCards, [...currentMelds, meld], results, config);
      }
    }

    // 4. 尝试组成普通顺子 (first, first+1, first+2)
    if (fVal <= 8) {
      const next1 = rest.find(c => c.type === fType && c.value === fVal + 1);
      const next2 = rest.find(c => c.type === fType && c.value === fVal + 2);
      if (next1 && next2) {
        // 如果是 1,2,3 并且上面已经处理过，则不重复计算为0胡顺子
        if (fVal !== 1) {
          const remainingCards = this.removeCards(cards, [first, next1, next2]);
          const meld: Meld = {
            id: `chi_seq_${first.id}`,
            type: 'CHI_SEQ',
            cards: [first, next1, next2],
            huxi: 0,
            isExposed: false
          };
          this.partitionTriplets(remainingCards, [...currentMelds, meld], results, config);
        }
      }
    }

    // 5. 尝试组成绞牌 (2大1小 或 1大2小 同数值)
    const otherType: CardType = fType === 'big' ? 'small' : 'big';
    const sameInRest = rest.filter(c => c.type === fType && c.value === fVal);
    const otherInRest = rest.filter(c => c.type === otherType && c.value === fVal);

    // 方案 A: 1个当前类型 + 2个对侧类型 (如 壹 + 一 + 一)
    if (config.allowJiaoHu && otherInRest.length >= 2) {
      const o1 = otherInRest[0];
      const o2 = otherInRest[1];
      const remainingCards = this.removeCards(cards, [first, o1, o2]);
      const meld: Meld = {
        id: `chi_jiao_${first.id}`,
        type: 'CHI_JIAO',
        cards: [first, o1, o2],
        huxi: 0,
        isExposed: false
      };
      this.partitionTriplets(remainingCards, [...currentMelds, meld], results, config);
    }

    // 方案 B: 2个当前类型 + 1个对侧类型 (如 壹 + 壹 + 一)
    if (config.allowJiaoHu && sameInRest.length >= 1 && otherInRest.length >= 1) {
      const s1 = sameInRest[0];
      const o1 = otherInRest[0];
      const remainingCards = this.removeCards(cards, [first, s1, o1]);
      const meld: Meld = {
        id: `chi_jiao_${first.id}`,
        type: 'CHI_JIAO',
        cards: [first, s1, o1],
        huxi: 0,
        isExposed: false
      };
      this.partitionTriplets(remainingCards, [...currentMelds, meld], results, config);
    }
  }

  /**
   * 从数组中移除指定的若干张牌实体
   */
  private static removeCards(source: Card[], toRemove: Card[]): Card[] {
    const removeIds = new Set(toRemove.map(c => c.id));
    return source.filter(c => !removeIds.has(c.id));
  }

  /**
   * 自动理牌与排柱算法 (将手牌组织为字牌经典的 7~10 列竖向堆叠)
   */
  public static organizeHandColumns(hand: Card[]): Card[][] {
    const columns: Card[][] = [];
    const usedIds = new Set<string>();

    const getAvailable = () => hand.filter(c => !usedIds.has(c.id));

    // 1. 优先提取 3张或4张相同牌 (坎 / 提)
    const counts = this.countCards(hand);
    for (const [key, count] of counts.entries()) {
      if (count >= 3) {
        const [typeStr, valStr] = key.split('_');
        const tType = typeStr as CardType;
        const tVal = parseInt(valStr);
        const matches = hand.filter(c => c.type === tType && c.value === tVal && !usedIds.has(c.id));
        if (matches.length >= 3) {
          const col = matches.slice(0, 4);
          col.forEach(c => usedIds.add(c.id));
          columns.push(col);
        }
      }
    }

    // 2. 提取 壹贰叁 和 一二三
    ['big', 'small'].forEach(tStr => {
      const type = tStr as CardType;
      const c1 = getAvailable().find(c => c.type === type && c.value === 1);
      const c2 = getAvailable().find(c => c.type === type && c.value === 2);
      const c3 = getAvailable().find(c => c.type === type && c.value === 3);
      if (c1 && c2 && c3) {
        columns.push([c1, c2, c3]);
        usedIds.add(c1.id);
        usedIds.add(c2.id);
        usedIds.add(c3.id);
      }
    });

    // 3. 提取 贰柒拾 和 二七十
    ['big', 'small'].forEach(tStr => {
      const type = tStr as CardType;
      const c2 = getAvailable().find(c => c.type === type && c.value === 2);
      const c7 = getAvailable().find(c => c.type === type && c.value === 7);
      const c10 = getAvailable().find(c => c.type === type && c.value === 10);
      if (c2 && c7 && c10) {
        columns.push([c2, c7, c10]);
        usedIds.add(c2.id);
        usedIds.add(c7.id);
        usedIds.add(c10.id);
      }
    });

    // 4. 提取 2张相同的对子
    const counts2 = this.countCards(getAvailable());
    for (const [key, count] of counts2.entries()) {
      if (count >= 2) {
        const [typeStr, valStr] = key.split('_');
        const tType = typeStr as CardType;
        const tVal = parseInt(valStr);
        const matches = getAvailable().filter(c => c.type === tType && c.value === tVal);
        if (matches.length >= 2) {
          const col = matches.slice(0, 2);
          col.forEach(c => usedIds.add(c.id));
          columns.push(col);
        }
      }
    }

    // 5. 提取连续的2张或3张顺子 (如 4-5 或 4-5-6)
    const avail = getAvailable().sort((a, b) => (a.type === b.type ? a.value - b.value : a.type === 'small' ? -1 : 1));
    for (let i = 0; i < avail.length; i++) {
      const c1 = avail[i];
      if (usedIds.has(c1.id)) continue;

      const c2 = avail.find(c => !usedIds.has(c.id) && c.type === c1.type && c.value === c1.value + 1);
      const c3 = c2 ? avail.find(c => !usedIds.has(c.id) && c.type === c1.type && c.value === c1.value + 2) : undefined;

      if (c1 && c2 && c3) {
        columns.push([c1, c2, c3]);
        usedIds.add(c1.id);
        usedIds.add(c2.id);
        usedIds.add(c3.id);
      } else if (c1 && c2) {
        columns.push([c1, c2]);
        usedIds.add(c1.id);
        usedIds.add(c2.id);
      }
    }

    // 6. 剩余散牌：尽量按同字同类归并，或单张独立一列 (最多10列)
    const remaining = getAvailable().sort((a, b) => (a.type === b.type ? a.value - b.value : a.type === 'small' ? -1 : 1));
    remaining.forEach(c => {
      // 找一个牌数小于3的列放进去，或开新列
      const openCol = columns.find(col => col.length < 3 && col[0].type === c.type && Math.abs(col[0].value - c.value) <= 2);
      if (openCol) {
        openCol.push(c);
      } else {
        columns.push([c]);
      }
    });

    return columns;
  }
}

